/**
 * Centralized Tolgee translations fetching utility
 * Used by both server and client to load translations from Tolgee API
 */

import { tolgeeApiKey, tolgeeApiUrl } from "./config";

type TolgeeTranslations = Record<string, string>;

// === ОБНОВЛЁННЫЕ TIMEOUT ЗНАЧЕНИЯ ===
const TOLGEE_INITIAL_TIMEOUT_MS = 1000;  // Первая попытка: 1 секунда
const TOLGEE_RETRY_TIMEOUT_MS = 2000;    // Retry попытки: 2 секунды

/**
 * Определяет какой timeout использовать в зависимости от типа запроса
 */
function getTolgeeTimeout(isRetry: boolean = false): number {
  const envTimeout = process.env.NEXT_PUBLIC_TOLGEE_TIMEOUT_MS;
  
  if (envTimeout) {
    const parsed = parseInt(envTimeout, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  
  return isRetry ? TOLGEE_RETRY_TIMEOUT_MS : TOLGEE_INITIAL_TIMEOUT_MS;
}

interface FetchTranslationsOptions {
  language: string;
  cache?: RequestCache;
  revalidate?: number;
  isRetry?: boolean; // Флаг retry попытки для увеличенного timeout
}

/**
 * Fetches translations from Tolgee API
 * @param options.isRetry - если true, используется увеличенный timeout
 * @returns Translations object or empty object if failed
 */
export async function fetchTolgeeTranslations(
  options: FetchTranslationsOptions
): Promise<TolgeeTranslations> {
  const { language, cache, revalidate, isRetry = false } = options;

  const apiUrl = tolgeeApiUrl;
  const apiKey = tolgeeApiKey;
  const projectId = process.env.NEXT_PUBLIC_TOLGEE_PROJECT_ID;

  if (!apiUrl || !apiKey || !projectId) {
    return {};
  }

  const timeout = getTolgeeTimeout(isRetry);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const url = `${apiUrl}v2/projects/${projectId}/translations/${language}`;

    const fetchOptions: RequestInit = {
      headers: {
        "X-API-Key": apiKey,
      },
      cache,
      signal: controller.signal,
    };

    if (revalidate !== undefined && typeof window === "undefined") {
      (fetchOptions as any).next = { revalidate };
    }

    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`[Tolgee] API error: ${response.status} ${response.statusText}`);
      return {};
    }

    const data = await response.json();

    if (!(data[language] && typeof data[language] === 'object')) {
      console.warn(`[Tolgee DEBUG] data["${language}"] is missing or not an object!`);
      console.warn(`[Tolgee DEBUG] Full data structure (first 500 chars): ${JSON.stringify(data).slice(0, 500)}`);
    }

    return data[language] || {};
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn(`[Tolgee DEBUG] Request aborted for language "${language}" (timeout ${timeout}ms)`);
      return {};
    }
    
    console.error("[Tolgee] Failed to fetch translations:", error);
    return {};
  }
}
